import {computed, reactive} from 'vue';

/** Die Query-Keys der Arbeitsdatei. EINZIGER Schreiber: dieses Modul (siehe Docstring unten). */
export const DOC_QUERY_KEYS = ['file', 'semitones', 'lineage', 'entry', 'document'] as const;
type DocKey = (typeof DOC_QUERY_KEYS)[number];

// Die Instanz, deren Stand `mirrorWorkingDocToUrl` in die URL schreibt (der Upload-Store ist der
// einzige Konsument; die jüngste Instanz gewinnt – in Tests entsteht je Pinia eine neue).
let activeMirror: (() => void) | null = null;

/** Schreibt den Datei-Stand im Speicher neu in die URL. Aufgerufen vom Router nach jeder Navigation. */
export function mirrorWorkingDocToUrl(): void {
    activeMirror?.();
}

function replaceUrl(url: URL): void {
    const next = url.pathname + url.search + url.hash;
    if (next === window.location.pathname + window.location.search + window.location.hash) return;
    // `history.state` mitgeben (kritisch): darin führt vue-router seine Zurück/Vor-Buchhaltung.
    window.history.replaceState(window.history.state, '', next);
}

/**
 * URL-gestützter Arbeitsdokument-Zustand (pro Tab konfliktfrei, überlebt Reload) – die einzige
 * Quelle der ?file/?semitones/?lineage/?entry/?document/?reuse-Parameter. Dauerhafte Persistenz
 * liegt in der Dateihistorie (IndexedDB).
 *
 * **Der Speicher ist die Wahrheit, die URL ein Spiegel** (kritisch – nicht auf VueUse
 * `useUrlSearchParams` rückbauen): gelesen wird die URL nur beim Anlegen, geschrieben werden NUR
 * die Datei-Keys, alle anderen Query-Keys (die der Router schreibt, z. B. die Akkord-Auswahl)
 * bleiben stehen. VueUse schrieb die ganze Query aus seinem eigenen Zustand – Router-Keys
 * verschwanden beim nächsten Edit – und holte bei Zurück/Vor die Datei-Werte des alten
 * History-Eintrags in den Speicher, obwohl längst eine andere Datei geladen war. Deshalb auch kein
 * `popstate`-Listener; nach einer Navigation stellt der Router die URL über
 * `mirrorWorkingDocToUrl` richtig.
 */
export function useWorkingDocUrlState() {
    const initial = new URLSearchParams(window.location.search);
    const state = reactive(
        Object.fromEntries(DOC_QUERY_KEYS.map((key) => [key, initial.get(key) || null])),
    ) as Record<DocKey, string | null>;

    function writeDocParams() {
        const url = new URL(window.location.href);
        for (const key of DOC_QUERY_KEYS) {
            const value = state[key];
            if (value) url.searchParams.set(key, value);
            else url.searchParams.delete(key);
        }
        replaceUrl(url);
    }
    activeMirror = writeDocParams;

    function docField(key: DocKey) {
        return computed<string | null>({
            get: () => state[key],
            set: (value) => {
                state[key] = value || null;
                writeDocParams();
            },
        });
    }

    function takeReuseId(): string | null {
        const url = new URL(window.location.href);
        const id = url.searchParams.get('reuse') || null;
        if (id) {
            url.searchParams.delete('reuse');
            replaceUrl(url);
        }
        return id;
    }

    const currentFileId = docField('file');
    const currentEntryId = docField('entry');
    const activeDocumentId = docField('document');

    const transposedSemitones = computed<number>({
        get: () => Number(state.semitones ?? 0) || 0,
        set: (value) => {
            state.semitones = value ? String(value) : null;
            writeDocParams();
        },
    });

    const lineage = docField('lineage');
    const lineageId = computed<string>({
        get: () => lineage.value || '',
        set: (value) => {
            lineage.value = value;
        },
    });

    return {currentFileId, transposedSemitones, lineageId, currentEntryId, activeDocumentId, takeReuseId};
}
